import { DrrOverviewTip } from '../campaign/drr-tip';
import { useState } from 'react';
import type { Tile } from '../api/types';
import { K } from '../kabinet/atoms';
import { useСвёртка } from '../kabinet/svyortka';
import { IconChevron } from '../ui/icons';
import { D } from '../kabinet/dobavki';

/**
 * Плитки над списком кампаний.
 *
 * ЗАЧЕМ ОНИ. Список отвечает на вопрос «что с каждой кампанией», и это
 * правильный первый вопрос. Но есть второй, который до сих пор не был
 * задан нигде: «а в сумме-то что?». Сорок строк с расходом по строке
 * не складываются глазами, и человек уходил считать их в Wildberries —
 * то есть в тот самый кабинет, вместо которого мы и написаны.
 *
 * ЗНАЧЕНИЕ И ПОДПИСЬ — РАЗНЫЕ ВЕЩИ. Число само по себе почти всегда
 * бессмысленно: «12,4 %» — это хороший ДРР или плохой, и от чего он
 * вообще посчитан? Подпись под каждой плиткой отвечает на это, и она
 * приходит с сервера вместе с числом, потому что там же и посчитано.
 *
 * ЦВЕТОМ ГОРЯТ НЕ ВСЕ. Если раскрасить восемь плиток, цвет перестанет
 * что-либо значить: спокойные — серые, и тогда одна красная видна
 * от двери. Уровень назначает сервер, здесь только краска.
 *
 * ПУСТОТА НЕ ПРИТВОРЯЕТСЯ НУЛЁМ. Пока плитки грузятся, на их месте
 * стоят серые заглушки того же размера, а не нули: ноль расхода —
 * это утверждение, и оно не должно возникать само по себе, из-за того
 * что сеть ещё не ответила.
 */

/**
 * ═══════════════════════════════════════════════════════════════════════
 * СВЁРНУТЫЕ ПОКАЗАТЕЛИ — ОДНА СТРОКА С ТЕМИ ЖЕ ЧИСЛАМИ.
 *
 * Передача 28.08.2026, раздел 2. Плитки при этом НЕ РИСУЮТСЯ ВОВСЕ,
 * и таблица получает около ста пятидесяти точек высоты.
 *
 * СВЁРТКА НЕ ПРЯЧЕТ ДАННЫЕ, А МЕНЯЕТ ПЛОТНОСТЬ — в этом вся мысль.
 * Расход, ДРР, заказы, впустую и цена заказа остаются на экране,
 * просто строкой вместо восьми карточек. Прячь она числа — это была бы
 * не свёртка, а «скрыть показатели», и вернуть их человек не догадался
 * бы.
 *
 * ПЯТЬ, А НЕ ВОСЕМЬ: в полосе стоят те же пять, что в эталоне, —
 * остальные три отвечают на вопросы, которые задают редко и не с этого
 * экрана.
 * ═══════════════════════════════════════════════════════════════════════
 */
const В_ПОЛОСЕ: ReadonlyArray<{ key: string; подпись: string }> = [
  { key: 'spend', подпись: 'Расход' },
  { key: 'drr', подпись: 'ДРР' },
  { key: 'orders', подпись: 'Заказы' },
  { key: 'waste', подпись: 'Без заказов' },
  { key: 'cpo', подпись: 'Заказ стоит' },
];

function ПолосаПоказателей({
  tiles,
  onOpen,
}: {
  tiles: Tile[];
  onOpen: () => void;
}) {
  /* ПОРЯДОК ПОЛОСЫ, А НЕ ПОРЯДОК ПЛИТОК. В эталоне полоса читается
     слева направо как одна фраза: «потратили — вот доля — вот заказы —
     вот сколько впустую — вот цена заказа». Отдать её порядку плиток
     значит получить другую фразу при первой же перестановке столбцов. */
  /* ОТБОР ПО КЛЮЧУ, А НЕ ПО ПОДПИСИ, И ЭТО НЕ ПРИДИРКА. Подписи плиток
     длиннее эталонных («Расход за период», «Расход без заказов»,
     «Цена заказа»), и первая сборка искала по ним — не находила ни одной
     и молча возвращала `null`. Свёртка выглядела как «кнопка не
     работает»; поймала сверка, у которой состояние не собралось.

     ПОДПИСЬ В ПОЛОСЕ СВОЯ И КОРОТКАЯ — так в эталоне: в строке из пяти
     пар «Расход за период» съедает место у чисел, ради которых полоса
     и заведена. */
  const строки = В_ПОЛОСЕ.map((п) => {
    const t = tiles.find((x) => x.key === п.key);
    return t === undefined ? null : { ...t, title: п.подпись };
  }).filter((t): t is Tile => t !== null);
  if (строки.length === 0) return null;

  return (
    <div className={K.полосаПоказателей} data-d="полоса-показателей">
      <span className="flex min-w-0 flex-1 flex-wrap items-baseline" style={{ gap: 'var(--sp-4)' }}>
        {строки.map((t) => {
          const краска = paintOf(t.level);
          return (
            <span
              key={t.title}
              className="inline-flex items-baseline whitespace-nowrap"
              style={{ gap: 6 }}
            >
              {/* КЕГЛЬ ИДЁТ СО СВОИМ ИНТЕРЛИНЬЯЖЕМ. Задать один `font-size`
                  значит оставить интерлиньяж от `body` — там он жёсткий,
                  20 точек: мелкая подпись выходит вдвое разреженной,
                  а число в 15 точек — строка в строку. Сторож
                  `tokens.spec.ts` держит пару полной. */}
              <span
                style={{
                  fontSize: 'var(--fs-sm)',
                  lineHeight: 'var(--lh-sm)',
                  color: 'var(--ink-3)',
                }}
              >
                {t.title}
              </span>
              <b
                style={{
                  fontSize: 'var(--fs-body)',
                  lineHeight: 'var(--lh-body)',
                  fontVariantNumeric: 'tabular-nums',
                  color: краска ? краска.ink : 'var(--ink)',
                }}
              >
                {t.value}
              </b>
            </span>
          );
        })}
      </span>
      <button
        type="button"
        data-d="кнопка-развернуть-показатели"
        className={K.кнопкаРазвернутьПоказатели}
        onClick={onOpen}
        title="Показать плитки показателей"
      >
        <IconChevron size={13} dir="down" />
        Показатели
      </button>
    </div>
  );
}

/**
 * Кнопка «свернуть показатели» — в ряду периода, а не в самих плитках.
 *
 * ТАК В ЭТАЛОНЕ, и так верно: свернуть надо ИМЕННО ТОГДА, когда плиток
 * на экране слишком много, — то есть нажимать приходится сверху, где
 * стоят остальные ручки о том, что показывать. Кнопка внутри плиток
 * уезжала бы вместе с ними.
 *
 * СОСТОЯНИЕ ОБЩЕЕ С `Dashboard` — через `useСвёртка` с тем же ключом,
 * а не через довод сверху. Довод пришлось бы тянуть через страницу
 * ради одного бита, который и так хранится в одном месте.
 */
export function КнопкаСвернутьПоказатели() {
  const [свёрнуто, переключить] = useСвёртка('показатели');
  const подпись = свёрнуто ? 'Показать показатели' : 'Свернуть показатели';
  return (
    <button
      type="button"
      data-d="кнопка-свернуть-показатели"
      className={K.кнопкаСвернутьПоказатели}
      onClick={переключить}
      title={подпись}
      aria-label={подпись}
      aria-pressed={свёрнуто}
    >
      {/* ОДИНОЧНЫЙ ШЕВРОН, А НЕ ДВОЙНОЙ (передача 28.08, раздел 1а):
          двойной в пятнадцать точек читался как крестик «закрыть». */}
      <IconChevron size={16} dir={свёрнуто ? 'down' : 'up'} />
    </button>
  );
}

export function Dashboard({
  tiles,
  loading,
  error,
}: {
  tiles: Tile[] | undefined;
  loading: boolean;
  error: boolean;
}) {
  const [свёрнуто, переключить] = useСвёртка('показатели');

  /* СВЁРНУТО — ПОЛОСА, И ТОЛЬКО ЕСЛИ ЕСТЬ ЧТО ПОКАЗАТЬ. Пока числа
     не пришли или пришёл отказ, свёртка ни при чём: показывать
     в полосе нечего, и обычные ветки ниже говорят об этом лучше. */
  if (свёрнуто && !error && !loading && tiles !== undefined && tiles.length > 0) {
    return <ПолосаПоказателей tiles={tiles} onOpen={переключить} />;
  }
  if (error) {
    return (
      <div
        className="px-4 py-3"
        style={{
          border: 'var(--hair) solid var(--bad)',
          borderRadius: 'var(--r-lg)',
          background: 'var(--bad-wash)',
          color: 'var(--bad-ink)',
          fontSize: 'var(--fs-sm)', lineHeight: 'var(--lh-sm)',
        }}
      >
        Сводка не посчиталась. Числа в списке ниже при этом настоящие — не сошлась
        только сумма по кабинету.
      </div>
    );
  }

  if (loading || !tiles) {
    return (
      <Grid>
        <div className={D.настольныеПлитки}>
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="rk-tile">
              <div className="rk-skeleton" style={{ height: 12, width: 96 }} />
              <div className="rk-skeleton" style={{ height: 24, width: 132 }} />
              <div className="rk-skeleton" style={{ height: 10, width: 150 }} />
            </div>
          ))}
        </div>
        {/* ЗАГЛУШКА ТЕЛЕФОНА — ТОГО ЖЕ РОСТА, ЧТО И ПЛИТКИ ТЕЛЕФОНА.
            Восемь настольных заглушек на 390 занимали те же девятьсот
            пикселей, и экран прыгал на восемьсот, когда числа
            приходили. */}
        <div className={D.телефонныеПлитки}>
          <div className={K.сеткаПлитокТелефон}>
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className={K.плиткаТелефон}>
                <div className="rk-skeleton" style={{ height: 12, width: 84 }} />
                <div className="rk-skeleton" style={{ height: 20, width: 104 }} />
              </div>
            ))}
          </div>
        </div>
      </Grid>
    );
  }

  return (
    <Grid>
      {/* НАСТОЛЬНЫЙ ВИД — сетка карточек с подписью под числом. */}
      <div className={D.настольныеПлитки}>
        {tiles.map((t) => (
          <TileBox key={t.key} tile={t} />
        ))}
      </div>
      {/* ТЕЛЕФОННЫЙ — другой, см. `Телефоном`. */}
      <div className={D.телефонныеПлитки}>
        <Телефоном tiles={tiles} />
      </div>
    </Grid>
  );
}

/** Сколько плиток видно на телефоне до нажатия «Ещё». Число эталона. */
const СРАЗУ = 4;

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПЛИТКИ НА ТЕЛЕФОНЕ: ЧЕТЫРЕ В ДВА СТОЛБЦА И КНОПКА «ЕЩЁ».
 *
 * ПОЧЕМУ НЕ ТЕ ЖЕ КАРТОЧКИ СТОЛБИКОМ. Так и было: восемь карточек
 * по 118 пикселей — 946 пикселей ДО первой строки таблицы, ради
 * которой экран открыт. Три с половиной экрана телефона прокрутки
 * мимо чисел, за которыми человек не приходил: он открыл кабинет
 * с телефона, чтобы посмотреть, не встала ли кампания.
 *
 * В эталоне на 390 — одна карточка, внутри сетка два на два,
 * в клетке подпись и число. Сто семьдесят два пикселя вместо
 * девятисот сорока шести.
 *
 * ПОЯСНЕНИЙ НА ТЕЛЕФОНЕ НЕТ, И ЭТО НЕ ПОТЕРЯ. Подпись под числом
 * («предел 18 % · живой расход + выручка») занимает три строки
 * из четырёх и на телефоне не читается, а обрезанная многоточием
 * не объясняет ничего. По кнопке «Ещё N показателей» возвращаются
 * остальные ЧИСЛА, а не пояснения: пояснение живёт на широком
 * экране, где ему есть место.
 *
 * «ЕЩЁ» РАСКРЫВАЕТ, А НЕ УВОДИТ. Стенд рисует кнопку без действия
 * (он статический), и увести её было бы некуда: отдельного экрана
 * показателей нет ни в эталоне, ни в ТЗ.
 * ═══════════════════════════════════════════════════════════════════════
 */
function Телефоном({ tiles }: { tiles: Tile[] }) {
  const [всё, setВсё] = useState(false);
  const видно = всё ? tiles : tiles.slice(0, СРАЗУ);
  const ещё = tiles.length - видно.length;

  return (
    <>
      <div className={K.сеткаПлитокТелефон}>
        {видно.map((t) => {
          const краска = paintOf(t.level);
          return (
            <div
              key={t.key}
              className={K.плиткаТелефон}
              style={краска ? { borderLeftColor: краска.край } : undefined}
            >
              <span className={K.подписьПлиткиТелефон}>{t.title}</span>
              <span
                className={K.значениеПлиткиТелефон}
                style={краска ? { color: краска.ink } : undefined}
              >
                {t.value}
                {t.key === 'drr' && <DrrOverviewTip hint={t.hint || t.note}/>}
              </span>
            </div>
          );
        })}
      </div>
      {(ещё > 0 || всё) && (
        <button type="button" className={K.кнопкаЕщёПоказатели} onClick={() => setВсё(!всё)}>
          {всё ? 'Свернуть показатели' : `Ещё ${ещё} ${показателей(ещё)}`}
        </button>
      )}
    </>
  );
}

/**
 * «Показатель», «показателя», «показателей» — по числу.
 *
 * Написать «Ещё 4 показателя» и оставить так нельзя: у половины
 * кабинетов плиток семь, и выйдет «Ещё 3 показателя» — верно,
 * а при пяти «Ещё 1 показателя» — нет.
 */
function показателей(n: number): string {
  const сотня = n % 100;
  if (сотня >= 11 && сотня <= 14) return 'показателей';
  const единица = n % 10;
  if (единица === 1) return 'показатель';
  if (единица >= 2 && единица <= 4) return 'показателя';
  return 'показателей';
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    // ЧЕТЫРЕ В РЯД НА ШИРОКОМ, ДВЕ НА УЗКОМ. Восемь в одну строку
    // превращают каждую плитку в колонку шириной со слово, и подпись
    // под числом — ради которой всё и затевалось — переносится
    // на четыре строки.
    /* СЕТКА ЭТАЛОНА: столько плиток в ряд, сколько влезает по 210 px.
       Прежде стояло жёстко «четыре на широком, две на узком», и на
       промежуточной ширине плитки то растягивались вдвое, то ломали
       ряд. */
    <div className={K.плиткиПоказателей} data-d="плитки-показателей">
      {children}
    </div>
  );
}

/** Дальше этой длины значение плитки печатается мельче. Число эталона. */
const ДЛИННОЕ = 12;

function TileBox({ tile }: { tile: Tile }) {
  const paint = paintOf(tile.level);
  return (
    // ЦВЕТ БЕРЁТ ТОЛЬКО РАМКУ И ЧИСЛО, А НЕ ЗАЛИВАЕТ ПЛИТКУ. Залитая
    // плитка спорит за внимание с таблицей под ней, ради которой экран
    // и открыт; тонкая цветная рамка видна от двери и не кричит.
    /* РАЗБОР — ПОД КУРСОРОМ НА ВСЕЙ ПЛИТКЕ, А НЕ НА ОДНОЙ ПОДПИСИ.
       Наводят на число, а не на мелкий текст под ним. */
    <div className={'rk-tile' + (paint ? ' ' + paint.tile : '')} title={tile.key === 'drr' ? undefined : tile.hint}>
      <span className="rk-tile-k">{tile.title}</span>
      {/* ЧИСЛО 26/32 — размер из образца. Прежде здесь стояло 30/36:
          взято из шкалы кеглей, а в образце у плитки свой размер,
          и на восьми плитках разница в четыре пикселя ломает ряд.

          ДЛИННОЕ — МЕЛЬЧЕ, и порог тоже из эталона: двенадцать знаков
          (`v.length > 12 ? '20px' : '26px'`). «112 542,82 ₽» — ровно
          двенадцать и печатается крупно; «не выгружается» —
          четырнадцать, и крупным оно ломалось на две строки,
          вытягивая плитку выше соседних. */}
      <span
        className={'rk-tile-v' + (tile.value.length > ДЛИННОЕ ? ' rk-tile-v-long' : '')}
        style={paint ? { color: paint.ink } : undefined}
      >
        {tile.value}
        {tile.key === 'drr' && <DrrOverviewTip hint={tile.hint || tile.note}/>}
      </span>
      {/* ПОДПИСЬ НЕ ОБРЕЗАЕТСЯ МНОГОТОЧИЕМ. Обрезанное «нужен отчёт
          продаж Wildberries — он пока…» не объясняет ничего, а именно
          объяснение здесь и есть содержание. */}
      <span className="rk-tile-s">
        {tile.key === 'drr' ? null : tile.note}
        {/* ═══════════════════════════════════════════════════════════
            СТРЕЛКА — СВЕТОФОРОМ, И НАПРАВЛЕНИЕ РЕШАЕТ СЕРВЕР.

            14.09.2026 владелец: «динамику в этом блоке с изменениями
            показывать светофором». Прежде «↑32 %» стояло серым куском
            внутри фразы, и прочитать его надо было целиком, чтобы
            понять, радоваться или нет.

            ЗЕЛЁНОЕ — НЕ «ВВЕРХ», А «ЛУЧШЕ». Рост заказов хорош, рост
            цены заказа плох; какая сторона хорошая, знает тот, кто
            считал число, и он же это говорит полем `лучшеВверх`.
            Гадать здесь по ключу плитки значит завести второе место,
            где это решается, — и однажды они разойдутся.
            ═══════════════════════════════════════════════════════ */}
        {tile.динамика !== undefined && (
          <>
            {' · '}
            {tile.динамика.за}{' '}
            {/* НОЛЬ — СЛОВАМИ И БЕЗ КРАСКИ. «↑0 %» сообщает о том, что
                сообщать нечего, а зелёный ноль ещё и хвалит за это.
                «Ровно столько же» — это ответ, и он не лучше и не хуже. */}
            {tile.динамика.процент === 0 ? (
              'без изменений'
            ) : (
              <span
                data-d="динамика-плитки"
                style={{
                  color:
                    tile.динамика.процент > 0 === tile.динамика.лучшеВверх
                      ? 'var(--good-ink)'
                      : 'var(--bad-ink)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {tile.динамика.процент > 0 ? '↑' : '↓'}
                {Math.abs(tile.динамика.процент)} %
              </span>
            )}
          </>
        )}
      </span>
    </div>
  );
}

/**
 * Чем красить плитку: рамка и число — РАЗНЫМИ шагами одного смысла.
 *
 * У рамки порог различимости 3:1, у числа — 4.5:1, и один цвет на оба
 * даёт либо блёклую рамку, либо нечитаемое число. Поэтому шага два:
 * `--good` для рамки и `--good-ink` для текста.
 */
/* `край` — цвет полоски слева у телефонной клетки: эталон красит
   её `var(--bad)`/`var(--good)`, то есть тем же шагом, что и рамку
   настольной плитки. */
function paintOf(level: Tile['level']): { tile: string; ink: string; край: string } | null {
  switch (level) {
    case 'bad':
      return { tile: 'rk-tile-bad', ink: 'var(--bad-ink)', край: 'var(--bad)' };
    case 'warn':
      return { tile: 'rk-tile-warn', ink: 'var(--warn-ink)', край: 'var(--warn)' };
    case 'ok':
      return { tile: 'rk-tile-good', ink: 'var(--good-ink)', край: 'var(--good)' };
    default:
      // СПОКОЙНОЕ — БЕЗ ЦВЕТА. Зелёная плитка «расход 12 000 ₽» ничего
      // не сообщает: расход не бывает хорошим или плохим сам по себе.
      return null;
  }
}
